import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';

import Button from '../../Button';
import Paragraph from '../../Paragraph';
import Heading from '../../Heading';
import { Row, Col } from '../Scaffold';
import { renderField } from '../../Inputs';


const number = value => (value && isNaN(Number(value)) ? 'Must be a number' : undefined);

const parsers = {
  number: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) <= 999 ? value.substring(0, 3) : '';
    }

    if (value) {
      return new RegExp(/^(?!0+$)\d{1,3}$/g).test(value) ? value : result;
    }
    return '';
  },
};

const aboveOne = (value) => {
  if (value < 0) {
    return 0;
  } else if (value > 999) {
    return parseInt(value.toString().substr(0, 3), 0);
  }
  return value;
};

const validate = (valueSet) => {
  const values = valueSet;
  const errors = {client: {}};

  if (!values.client) values.client = {};
  // if (!values.client.title) errors.client.title = 'Please select your title';
  if (!values.client.first_name) errors.client.first_name = 'Please enter your first name';
  if (!values.client.last_name) errors.client.last_name = 'Please enter your last name';
  if (!values.client.gender) errors.client.gender = 'Please enter your gender';
  if (values.client.gender === 'Other' && !values.client.gender_other) errors.client.gender = 'Please enter your gender';
  if (!values.client.dob) errors.client.dob = 'Please enter a valid date of birth';
  if (!values.client.gp_practice_id) errors.client.gp_practice_id = 'Please enter your GP Practice';
  if (!values.client.ethnicity) errors.client.ethnicity = 'Please enter your ethnicity';
  if (!values.client.email) errors.client.email = 'Please enter your email address';
  if (!values.client.phone_mobile) errors.client.phone_mobile = 'Please enter your mobile phone number';
  if (!values.client.leave_message) errors.client.leave_message = 'Please select whether we can leave a message or not';
  if (!values.client.address1) errors.client.address1 = 'Please enter your address';
  if (!values.client.town) errors.client.town = 'Please enter your town';
  if (!values.client.postcode) errors.client.postcode = 'Please enter your postcode';

  if (!values.client.smoking) errors.client.smoking = 'Please select if you are currently smoking';
  if (!values.client.smoking_type_id) errors.client.smoking_type_id = 'Please select the type of your smoking';
  if (!values.client.smoking_quantity) errors.client.smoking_quantity = 'Please fill in the smoking quantity (max: 999)';

  if (!values.client.receive_marketing) errors.client.receive_marketing = 'Please select if you’d like to receive marketing emails or not';
  if (!values.client.pregnant) errors.client.pregnant = 'Please select if you are pregnant or not';

  if (!values.client.password) {
    errors.client.password = 'Please enter a password of at least 8 characters';
  } else if (values.client.password.length < 8) {
    errors.client.password = 'Please enter a password of at least 8 characters';
  }

  if (!values.client.confirm_password) {
    errors.client.confirm_password = 'Please enter a confirmed password of at least 8 characters';
  } else if (values.client.confirm_password.length < 8) {
    errors.client.confirm_password = 'Please enter a confirmed password of at least 8 characters';
  } else if (values.client.password !== values.client.confirm_password) {
    errors.client.confirm_password = 'Please ensure passwords match';
  }

  if (values.client.dob) {
    const now = moment();
    const age = moment.duration(now.diff(moment(values.client.dob)));
    if (age.asYears() < 18) {
      errors.client.dob = 'Sorry, you must be over 18 to register';
    }
  }

  const validPostcodes = ['BA', 'BH', 'DT', 'EX', 'PL', 'SP', 'TA', 'TQ'];
  if (values.client.postcode) {
    const shortPostcode = values.client.postcode.toUpperCase().substr(0, 2);
    if (validPostcodes.indexOf(shortPostcode) === -1) {
      errors.client.postcode = 'Sorry, we’re only available to clients who live in Dorset';
    }
  }

  if (!values.client.email) {
    errors.client.email = 'Please enter your email address';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.client.email)) {
    errors.client.email = 'Please enter a valid email address';
  }
  return errors;
};

class ClientInfo extends React.PureComponent {
    state = {
      dob: '',
      startDate: null,
      pregnant: '',
      gender: null,
      smoking: this.props.smoking,
      smokingAmount: '',
      smokingType: '',
    };

    componentDidMount() {
      if (window.pageYOffset > this.sectionRef.offsetTop) {
        window.scrollTo({ top: this.sectionRef.offsetTop, behavior: 'smooth' });
      }
    }

    render() {
      const { handleSubmit, previousPage } = this.props;
      return (
        <Row classes="client-info" setRef={(sectionRef) => { this.sectionRef = sectionRef; }}>
          <Heading classes="header-text smaller purple" center><span id="register3">Tell us a little about yourself</span></Heading>
          <Paragraph classes="width-limited" center>Let’s keep things easy to start with, could you tell us a little bit about yourself so we have a better understanding of who you are.</Paragraph>
          <Paragraph classes="width-limited notice-text" center>Please note all fields marked with an asterisk (*) are required.</Paragraph>
          <form onSubmit={handleSubmit} className="form-register">
            <Row>
              <Col>
                {/* <Field name="client[title]" type="select" params={{ dataKey: 'title' }} component={renderField} label="Your title *" /> */}
                <Field name="client[first_name]" type="text" component={renderField} label="Your first name *" classes="text-input-field" />
                <Field name="client[last_name]" type="text" component={renderField} label="Your last name *" classes="text-input-field" />
                <div className="section-divider" />
                <Field name="client[dob]" type="date" component={renderField} label="Your date of birth *" />
                <Field
                  name="client[gender]"
                  type="select"
                  params={{ dataKey: 'gender' }}
                  component={renderField}
                  onChange={(e, gender) => this.setState({ gender })}
                  label="Your gender *"
                />
                {this.state.gender === 'Prefer to self describe as...' ?
                  <Field name="client[gender_other]" type="text" component={renderField} label="Your gender *" classes="text-input-field" />
                : null }

                <Field
                  name="client[gp_practice_id]"
                  type="select"
                  params={{ dataKey: 'gpSurgery' }}
                  component={renderField}
                  label="Your GP practice *"
                />
                <Field name="client[ethnicity]" type="select" params={{ dataKey: 'ethnicity' }} component={renderField} label="Your ethnicity *" />
                <div className="section-divider" />
                <Field name="client[email]" type="email" component={renderField} label="Your email address *" classes="text-input-field" />
                <Field name="client[phone_mobile]" type="text" component={renderField} label="Your mobile number *" classes="text-input-field" />
                <Field
                  name="client[leave_message]"
                  type="select"
                  params={{ dataKey: 'LeaveMessage' }}
                  component={renderField}
                  label="Are you happy for us to leave a message if you're not available? *"
                />
                <div className="section-divider" />
              </Col>
              <Col>
                <Field name="client[address1]" type="text" component={renderField} label="Your address 1 *" classes="text-input-field" />
                <Field name="client[address2]" type="text" component={renderField} label="Your address 2" classes="text-input-field" />
                <Field name="client[town]" type="text" component={renderField} label="Your town/city *" classes="text-input-field" />
                <Field name="client[postcode]" type="text" component={renderField} label="Your postcode *" classes="text-input-field" />

                <div className="section-divider" key="1" />
                <Field
                  name="client[smoking_type_id]"
                  type="select"
                  params={{ dataKey: 'SmokingType', level: 2, referralId: this.state.smoking }}
                  component={renderField}
                  label="What do you smoke? *"
                  onChange={(ks, smokingType) => this.setState({ smokingType })}
                />

                <Field
                  name="client[smoking_quantity]"
                  validate={[number]}
                  type="text"
                  component={renderField}
                  normalize={aboveOne}
                  parse={parsers.number}
                  classes="text-input-field"
                  label="How many do you smoke a day? *"
                  onChange={(ks, smokingAmount) => this.setState({ smokingAmount })}
                />
                <div className="section-divider" key="1" />


                <Field
                  name="client[receive_marketing]"
                  type="select"
                  params={{dataKey: 'contact'}}
                  component={renderField}
                  label="Are you happy to receive marketing emails from us? *"
                />
                <Field
                  name="client[activity_pregnant]"
                  type="select"
                  params={{dataKey: 'LeaveMessage'}}
                  component={renderField}
                  label="Are you currently pregnant? *"
                  onChange={(ks, pregnant) => this.setState({ pregnant })}
                />
                <div className="section-divider" />
                <Field name="client[password]" type="password" component={renderField} label="Create a password *" classes="text-input-field" />
                <Field name="client[confirm_password]" type="password" component={renderField} label="Confirm your password *" classes="text-input-field" />
              </Col>
            </Row>
            <Paragraph classes="button-groups divideline" center>
              <Button classes={['color-button']} label="Next" onClick={handleSubmit} />
              <Button classes={['underline-button']} label="Back" onClick={previousPage} />
            </Paragraph>
          </form>
        </Row>
      );
    }
}

ClientInfo.propTypes = {
  previousPage: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  smoking: PropTypes.string.isRequired,
  pregnant: PropTypes.string.isRequired,
};

export default reduxForm({
  form: 'registrationPilot', // <------ same form name
  destroyOnUnmount: false, // <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  validate,
})(ClientInfo);



// WEBPACK FOOTER //
// ./src/Components/PilotRegister/FormPages/ClientInfo.jsx